import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import {
  Sparkles, Loader, RotateCcw, AlertTriangle, CheckCircle2, XCircle,
  Activity, MessageSquare, ArrowRight, Clock, History,
  ChevronLeft, ChevronRight, ChevronDown,
  Copy, Check,
} from 'lucide-react';

// ============================================================
// LEAD DIAGNOSTIC CARD
//
// Renders the per-lead AI diagnostic with a smart-history model.
//
// Endpoints:
//   GET  /leads/:id/diagnostic          — latest entry + staleness signal
//   GET  /leads/:id/diagnostic/history  — paginated previous entries
//   POST /leads/:id/diagnostic          — smart-regen (INSERT or reuse-latest)
//
// "Run AI Diagnostic" calls POST. The backend decides whether to insert
// a new entry (latest is older than the admin-configurable threshold OR
// the lead has been edited since the latest entry) or return the latest
// one unchanged. When it returns the latest we surface a small inline
// note so the user knows nothing was actually re-run.
//
// Generation is admin/TL-only. The caller passes canRegenerate={isAdminOrTL},
// so the "Run AI Diagnostic" button renders only for admin and team_lead;
// bde and viewer see the latest diagnostic + history + Copy but no run
// button. The backend POST is requireAdminOrTL-gated too, so hiding the
// button is UX — the 403 is the real guard.
// ============================================================

const HISTORY_PAGE_SIZE = 5;

// Friendly labels for the pattern_read enum. The enum values themselves
// stay as the DB taxonomy; we translate to plain English for the UI.
const PATTERN_META = {
  pain_anchored: {
    label: 'Listening first, selling second',
    color: '#065F46', bg: '#ECFDF5', border: '#A7F3D0',
    Icon: CheckCircle2,
    summary: 'The BDE understood what the prospect actually needs, then suggested solutions for that. This is the right shape.',
  },
  solution_pitching: {
    label: 'Selling before listening',
    color: '#991B1B', bg: '#FEF2F2', border: '#FECACA',
    Icon: XCircle,
    summary: 'The BDE is suggesting solutions before understanding the prospect’s real problem. This usually loses the lead.',
  },
  mixed: {
    label: 'A mix of both',
    color: '#92400E', bg: '#FFFBEB', border: '#FDE68A',
    Icon: Activity,
    summary: 'Sometimes the BDE focused on the prospect’s real problem, sometimes jumped to selling. Worth a closer look.',
  },
  insufficient_data: {
    label: 'Not enough conversation yet',
    color: '#6B7280', bg: '#F9FAFB', border: '#E5E7EB',
    Icon: MessageSquare,
    summary: 'There isn’t enough conversation yet to tell how this is going.',
  },
};

const formatTimestamp = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const formatAgo = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

// Backend returns age_hours rounded to 2 decimals; "0.01 hours ago"
// reads weird for sub-minute deltas. Collapse to minutes below 1h.
const formatReuseAge = (hours) => {
  if (typeof hours !== 'number' || !Number.isFinite(hours)) return '';
  if (hours < 1 / 60) return 'less than a minute ago';
  if (hours < 1) {
    const mins = Math.max(1, Math.round(hours * 60));
    return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  }
  const rounded = Math.round(hours);
  return `${rounded} hour${rounded === 1 ? '' : 's'} ago`;
};

// ============================================================
// MARKDOWN EXPORT
//
// Renders one diagnostic history entry as clean markdown for clipboard
// paste. Header is one short line (generated timestamp + lead identifier
// so the paste isn't ambiguous about WHICH lead it's about); body is
// every section in the schema, with empty arrays rendered as "None."
// so the structure stays consistent across copies.
// ============================================================
const formatGeneratedShort = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

const buildDiagnosticMarkdown = (entry, prospectName, company) => {
  const d = entry.diagnostic || {};
  const lines = [];

  const leadBit = [prospectName, company].filter(Boolean).join(' — ');
  lines.push(
    `_Generated ${formatGeneratedShort(entry.generated_at)}${leadBit ? ' · Lead: ' + leadBit : ''}_`,
  );
  lines.push('');

  // Current state
  lines.push('## Current state');
  lines.push(d.current_state_summary ? String(d.current_state_summary).trim() : 'None.');
  lines.push('');

  // Pattern read
  lines.push('## Pattern read');
  const patternLabel = (PATTERN_META[d.pattern_read] && PATTERN_META[d.pattern_read].label) || d.pattern_read || 'None.';
  lines.push(`**${patternLabel}**`);
  if (d.pattern_explanation) {
    lines.push('');
    lines.push(String(d.pattern_explanation).trim());
  }
  lines.push('');

  // Field mismatches
  lines.push('## Field mismatches');
  if (Array.isArray(d.field_mismatches) && d.field_mismatches.length > 0) {
    d.field_mismatches.forEach((m) => {
      lines.push(`- **${m.field}**`);
      lines.push(`  - Tool says: ${m.db_value}`);
      lines.push(`  - Conversation says: ${m.conversation_says}`);
      if (m.explanation) lines.push(`  - ${m.explanation}`);
    });
  } else {
    lines.push('None.');
  }
  lines.push('');

  // Stale internal notes
  lines.push('## Stale internal notes');
  if (Array.isArray(d.stale_internal_notes) && d.stale_internal_notes.length > 0) {
    d.stale_internal_notes.forEach((n) => {
      lines.push(`- "${n.note_text}"`);
      if (n.contradicted_by) lines.push(`  - Contradicted by: ${n.contradicted_by}`);
      if (n.explanation) lines.push(`  - ${n.explanation}`);
    });
  } else {
    lines.push('None.');
  }
  lines.push('');

  // What's working
  lines.push("## What's working");
  if (Array.isArray(d.whats_working) && d.whats_working.length > 0) {
    d.whats_working.forEach((s) => lines.push(`- ${s}`));
  } else {
    lines.push('None.');
  }
  lines.push('');

  // What's stuck
  lines.push("## What's stuck");
  if (Array.isArray(d.whats_stuck) && d.whats_stuck.length > 0) {
    d.whats_stuck.forEach((s) => lines.push(`- ${s}`));
  } else {
    lines.push('None.');
  }
  lines.push('');

  // Recommended next steps
  lines.push('## Recommended next steps');
  if (Array.isArray(d.next_steps) && d.next_steps.length > 0) {
    d.next_steps.forEach((step, i) => lines.push(`${i + 1}. ${step}`));
  } else {
    lines.push('None.');
  }

  return lines.join('\n');
};

// ============================================================
// COPY BUTTON — per-entry. Own "copied" state so clicks on different
// entries don't interfere with each other.
// ============================================================
const CopyButton = ({ entry, prospectName, company }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      const md = buildDiagnosticMarkdown(entry, prospectName, company);
      await navigator.clipboard.writeText(md);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      alert('Could not copy to clipboard. Select and copy manually.');
    }
  };

  return (
    <button
      onClick={handleCopy}
      className="btn btn-secondary btn-sm"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        flexShrink: 0,
      }}
      title="Copy this diagnostic as markdown"
    >
      {copied
        ? <><Check size={12} /> Copied</>
        : <><Copy size={12} /> Copy</>}
    </button>
  );
};

// ============================================================
// DIAGNOSTIC DETAIL — the structured output rendering. Used for the
// latest entry (always shown) and for older entries (revealed on
// click in HistoryEntryCard).
// ============================================================
const Section = ({ title, children, accent }) => (
  <div style={{ marginTop: 16 }}>
    <div style={{
      fontSize: 12, fontWeight: 600, color: accent || '#6B7280',
      textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8,
    }}>{title}</div>
    {children}
  </div>
);

const Bullets = ({ items, emptyText }) => {
  if (!items || items.length === 0) {
    return (
      <div style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic' }}>
        {emptyText}
      </div>
    );
  }
  return (
    <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14, color: '#111827' }}>
      {items.map((item, i) => (
        <li key={i} style={{ marginBottom: 6, lineHeight: 1.5 }}>{item}</li>
      ))}
    </ul>
  );
};

const FieldMismatch = ({ mismatch }) => (
  <div style={{
    padding: 12, background: '#FEF2F2',
    border: '1px solid #FECACA', borderLeft: '4px solid #EF4444',
    borderRadius: 6, marginBottom: 8, fontSize: 14,
  }}>
    <div style={{ fontWeight: 600, color: '#991B1B', marginBottom: 4 }}>
      {mismatch.field}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: 6, color: '#374151' }}>
      <div style={{ fontSize: 12, color: '#6B7280' }}>Tool says:</div>
      <div style={{ fontSize: 13 }}>{mismatch.db_value}</div>
      <div style={{ fontSize: 12, color: '#6B7280' }}>Conversation says:</div>
      <div style={{ fontSize: 13 }}>{mismatch.conversation_says}</div>
    </div>
    <div style={{ fontSize: 13, color: '#374151', marginTop: 6, fontStyle: 'italic' }}>
      {mismatch.explanation}
    </div>
  </div>
);

const StaleNote = ({ note }) => (
  <div style={{
    padding: 12, background: '#FFFBEB',
    border: '1px solid #FDE68A', borderLeft: '4px solid #F59E0B',
    borderRadius: 6, marginBottom: 8, fontSize: 14,
  }}>
    <div style={{ fontWeight: 600, color: '#92400E', marginBottom: 4, fontSize: 12 }}>
      Stale note
    </div>
    <div style={{ color: '#374151', marginBottom: 6, fontStyle: 'italic' }}>
      “{note.note_text}”
    </div>
    <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>
      Contradicted by: <span style={{ color: '#374151' }}>{note.contradicted_by}</span>
    </div>
    <div style={{ fontSize: 13, color: '#374151' }}>{note.explanation}</div>
  </div>
);

const DiagnosticDetail = ({ diagnostic }) => {
  const pattern = PATTERN_META[diagnostic.pattern_read] || PATTERN_META.insufficient_data;
  const PatternIcon = pattern.Icon;
  return (
    <>
      {/* Pattern read banner */}
      <div style={{
        marginTop: 16, display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: 12, background: pattern.bg,
        border: `1px solid ${pattern.border}`,
        borderLeft: `4px solid ${pattern.color}`, borderRadius: 6,
      }}>
        <PatternIcon size={18} color={pattern.color} style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: 12, fontWeight: 600, color: pattern.color,
            textTransform: 'uppercase', letterSpacing: '0.05em',
          }}>
            Reading: {pattern.label}
          </div>
          <div style={{ fontSize: 14, color: '#111827', marginTop: 4 }}>
            {diagnostic.pattern_explanation || pattern.summary}
          </div>
        </div>
      </div>

      <Section title="Current state">
        <div style={{ fontSize: 14, color: '#111827', lineHeight: 1.55 }}>
          {diagnostic.current_state_summary}
        </div>
      </Section>

      {diagnostic.field_mismatches && diagnostic.field_mismatches.length > 0 && (
        <Section title="Where the tool doesn't match the conversation" accent="#991B1B">
          {diagnostic.field_mismatches.map((m, i) => (
            <FieldMismatch key={i} mismatch={m} />
          ))}
        </Section>
      )}

      {diagnostic.stale_internal_notes && diagnostic.stale_internal_notes.length > 0 && (
        <Section title="Old notes that aren't true anymore" accent="#92400E">
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 12, color: '#92400E', marginBottom: 8,
          }}>
            <AlertTriangle size={12} />
            Notes the later conversation contradicts
          </div>
          {diagnostic.stale_internal_notes.map((n, i) => (
            <StaleNote key={i} note={n} />
          ))}
        </Section>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginTop: 16 }}>
        <div style={{
          padding: 12, background: '#ECFDF5',
          border: '1px solid #A7F3D0', borderRadius: 6,
        }}>
          <div style={{
            fontSize: 12, fontWeight: 600, color: '#065F46',
            textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8,
          }}>What's working</div>
          <Bullets items={diagnostic.whats_working} emptyText="Nothing notable yet." />
        </div>
        <div style={{
          padding: 12, background: '#FEF2F2',
          border: '1px solid #FECACA', borderRadius: 6,
        }}>
          <div style={{
            fontSize: 12, fontWeight: 600, color: '#991B1B',
            textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8,
          }}>What's stuck</div>
          <Bullets items={diagnostic.whats_stuck} emptyText="Nothing flagged as stuck." />
        </div>
      </div>

      {Array.isArray(diagnostic.next_steps) && diagnostic.next_steps.length > 0 && (
        <Section title="Recommended next steps" accent="#1D4ED8">
          <ol style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
            {diagnostic.next_steps.map((step, i) => (
              <li key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '10px 12px',
                background: '#EFF6FF', border: '1px solid #BFDBFE',
                borderLeft: '4px solid #3B82F6', borderRadius: 6,
                marginBottom: 8,
              }}>
                <div style={{
                  flexShrink: 0, width: 22, height: 22, borderRadius: 11,
                  background: '#3B82F6', color: '#FFFFFF',
                  fontSize: 12, fontWeight: 600,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginTop: 1,
                }}>{i + 1}</div>
                <div style={{ flex: 1, fontSize: 14, color: '#111827', lineHeight: 1.5 }}>
                  {step}
                </div>
                <ArrowRight size={16} color="#3B82F6" style={{ flexShrink: 0, marginTop: 4 }} />
              </li>
            ))}
          </ol>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
            Ordered most-important first.
          </div>
        </Section>
      )}
    </>
  );
};

// ============================================================
// ENTRY METADATA STRIP
// ============================================================
const EntryMeta = ({ entry }) => {
  const parts = [];
  if (entry.generated_at) {
    parts.push(`Generated ${formatTimestamp(entry.generated_at)} (${formatAgo(entry.generated_at)})`);
  }
  if (entry.generated_by_name) parts.push(`by ${entry.generated_by_name}`);
  else if (entry.generated_by_user_id === null) parts.push('auto-regenerated');
  if (entry.model_used) parts.push(`model: ${entry.model_used}`);
  return (
    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
      {parts.join(' · ')}
    </div>
  );
};

// ============================================================
// HISTORY ENTRY CARD — collapsed by default, expand to reveal the
// full DiagnosticDetail. Used for older entries.
// ============================================================
const HistoryEntryCard = ({ entry, prospectName, company }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{
      border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden',
      marginBottom: 8,
    }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          background: '#F9FAFB',
          padding: '10px 14px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 10,
        }}
      >
        {open ? <ChevronDown size={16} color="#6B7280" /> : <ChevronRight size={16} color="#6B7280" />}
        <Clock size={14} color="#6B7280" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, color: '#111827', fontWeight: 500 }}>
            {formatTimestamp(entry.generated_at)}
          </div>
          <div style={{ fontSize: 12, color: '#6B7280' }}>
            {[
              entry.generated_by_name && `by ${entry.generated_by_name}`,
              !entry.generated_by_name && entry.generated_by_user_id === null && 'auto-regenerated',
              entry.model_used,
            ].filter(Boolean).join(' · ')}
          </div>
        </div>
        <CopyButton entry={entry} prospectName={prospectName} company={company} />
      </div>
      {open && entry.diagnostic && (
        <div style={{ padding: '0 14px 14px' }}>
          <DiagnosticDetail diagnostic={entry.diagnostic} />
        </div>
      )}
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const LeadDiagnosticCard = ({ leadId, canRegenerate, prospectName, company }) => {
  const [latest, setLatest] = useState(null);
  const [leadUpdatedSince, setLeadUpdatedSince] = useState(false);

  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);

  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [reuseHint, setReuseHint] = useState(null);

  const fetchLatest = useCallback(async () => {
    const res = await api.get(`/leads/${leadId}/diagnostic`);
    setLatest(res.data.latest);
    setLeadUpdatedSince(!!res.data.lead_updated_since_generation);
  }, [leadId]);

  const fetchHistory = useCallback(async (page) => {
    setHistoryLoading(true);
    try {
      const res = await api.get(`/leads/${leadId}/diagnostic/history`, {
        params: { page, page_size: HISTORY_PAGE_SIZE },
      });
      setHistory(res.data.entries || []);
      setHistoryTotal(res.data.total || 0);
      setHistoryTotalPages(res.data.total_pages || 1);
    } finally {
      setHistoryLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    setLoading(true);
    setError('');
    setReuseHint(null);
    setHistoryPage(1);
    Promise.all([fetchLatest(), fetchHistory(1)])
      .catch((e) => setError(e.response?.data?.error || 'Failed to load diagnostic.'))
      .finally(() => setLoading(false));
  }, [leadId, fetchLatest, fetchHistory]);

  useEffect(() => {
    if (loading) return;
    fetchHistory(historyPage).catch(() => {});
  }, [historyPage, fetchHistory, loading]);

  const handleRun = async () => {
    if (running) return;
    setRunning(true);
    setError('');
    setReuseHint(null);
    try {
      const res = await api.post(`/leads/${leadId}/diagnostic`);
      setLatest(res.data.latest);
      setLeadUpdatedSince(!!res.data.lead_updated_since_generation);
      if (res.data.created_new) {
        setHistoryPage(1);
        await fetchHistory(1);
      } else {
        setReuseHint({
          threshold_hours: res.data.reuse_reason?.threshold_hours,
          age_hours: res.data.reuse_reason?.age_hours,
        });
      }
    } catch (e) {
      setError(e.response?.data?.error || 'AI diagnostic failed. Try again in a moment.');
    } finally {
      setRunning(false);
    }
  };

  const Header = ({ subtitle, action }) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      alignItems: 'flex-start', flexWrap: 'wrap', gap: 12,
    }}>
      <div>
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={18} color="#3B82F6" /> AI Diagnostic
        </h3>
        {subtitle && (
          <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>{subtitle}</div>
        )}
      </div>
      {action}
    </div>
  );

  if (loading) {
    return (
      <div className="card" style={{ marginTop: 16 }}>
        <Header subtitle="Loading…" />
      </div>
    );
  }

  // --- empty / never-run state ---
  if (!latest) {
    return (
      <div className="card" style={{ marginTop: 16 }}>
        <Header
          subtitle="No diagnostic yet. Run it to surface mismatches, stale notes, and the BDE's pattern read."
          action={canRegenerate && (
            <button
              onClick={handleRun}
              disabled={running}
              className="btn btn-primary"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                opacity: running ? 0.6 : 1,
                cursor: running ? 'default' : 'pointer',
              }}
            >
              {running
                ? <><Loader size={14} /> Running…</>
                : <><Sparkles size={14} /> Run AI Diagnostic</>}
            </button>
          )}
        />
        {error && (
          <div style={{
            marginTop: 12, padding: 8, background: '#FEE2E2',
            color: '#991B1B', borderRadius: 6, fontSize: 13,
          }}>{error}</div>
        )}
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <Header
        action={canRegenerate && (
          <button
            onClick={handleRun}
            disabled={running}
            className="btn btn-secondary"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              opacity: running ? 0.6 : 1,
              cursor: running ? 'default' : 'pointer',
            }}
          >
            {running
              ? <><Loader size={14} /> Running…</>
              : <><RotateCcw size={14} /> Run AI Diagnostic</>}
          </button>
        )}
      />

      {/* Staleness banner — page-load read. Lead was edited after the
          latest diagnostic was generated. The "click Run" prompt only
          makes sense for admin/TL (the roles with the button), so bde and
          viewer get an informational note instead of an instruction to
          click a button they can't see. */}
      {leadUpdatedSince && !running && (
        <div style={{
          marginTop: 12, padding: 10, background: '#FFFBEB',
          border: '1px solid #FDE68A', borderRadius: 6,
          fontSize: 13, color: '#92400E',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertTriangle size={14} />
          <span>
            This lead has been edited since the latest diagnostic was generated.
            {canRegenerate
              ? <> Click <strong>Run AI Diagnostic</strong> to refresh.</>
              : ' It may not reflect the latest changes.'}
          </span>
        </div>
      )}

      {/* Reuse hint — backend declined the regenerate. */}
      {reuseHint && !running && (
        <div style={{
          marginTop: 12, padding: 10, background: '#EFF6FF',
          border: '1px solid #BFDBFE', borderRadius: 6,
          fontSize: 13, color: '#1D4ED8',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Clock size={14} />
          <span>
            Showing the latest diagnostic from {formatReuseAge(reuseHint.age_hours)} — the lead hasn't changed since,
            and the threshold is {reuseHint.threshold_hours} hour{reuseHint.threshold_hours === 1 ? '' : 's'}.
          </span>
        </div>
      )}

      {error && (
        <div style={{
          marginTop: 12, padding: 8, background: '#FEE2E2',
          color: '#991B1B', borderRadius: 6, fontSize: 13,
        }}>{error}</div>
      )}

      {/* LATEST DIAGNOSTIC — always expanded */}
      <div style={{ marginTop: 12 }}>
        <div style={{
          padding: '8px 12px', background: '#F0FDF4',
          border: '1px solid #BBF7D0', borderLeft: '4px solid #16A34A',
          borderRadius: 6,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#166534' }}>
              Latest diagnostic
            </div>
            <EntryMeta entry={latest} />
          </div>
          <CopyButton entry={latest} prospectName={prospectName} company={company} />
        </div>
        <DiagnosticDetail diagnostic={latest.diagnostic} />
      </div>

      {/* HISTORY — collapsed entries below */}
      {historyTotal > 0 && (
        <div style={{ marginTop: 24, borderTop: '1px solid #E5E7EB', paddingTop: 16 }}>
          <div style={{
            fontSize: 14, fontWeight: 600, color: '#111827',
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
          }}>
            <History size={16} color="#6B7280" /> Previous diagnostics ({historyTotal})
          </div>

          {history.map((entry) => (
            <HistoryEntryCard
              key={entry.id}
              entry={entry}
              prospectName={prospectName}
              company={company}
            />
          ))}

          {historyLoading && (
            <div style={{ fontSize: 13, color: '#6B7280', padding: '8px 4px' }}>
              Loading…
            </div>
          )}

          {historyTotalPages > 1 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12,
              marginTop: 12, fontSize: 13, color: '#6B7280',
            }}>
              <button
                onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                disabled={historyPage <= 1 || historyLoading}
                className="btn btn-secondary btn-sm"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <span>Page {historyPage} of {historyTotalPages}</span>
              <button
                onClick={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))}
                disabled={historyPage >= historyTotalPages || historyLoading}
                className="btn btn-secondary btn-sm"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LeadDiagnosticCard;
